<template>
  <div class="dropdown-container" ref="dropdown">
    <label :for="id" v-if="label">{{ label }}</label>

    <div class="custom-dropdown" :class="{ 'is-open': isOpen, 'is-invalid': showError }">
      <button type="button" :id="id" class="dropdown-toggle" @click="toggleDropdown" :disabled="disabled">
        <span class="selected-text">
          {{ selectedLabel || placeholder }}
        </span>
        <i class="dropdown-arrow" :class="{ 'is-open': isOpen }"></i>
      </button>

      <div class="dropdown-menu" v-if="isOpen" :style="{ maxHeight: maxHeight + 'px' }">
        <div class="search-dropdown">
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Search..."
            class="search-field"
            ref="searchInput"
          />
        </div>
        <div class="dropdown-items">
          <template v-if="filteredGrades.length > 0">
            <div v-for="option in filteredGrades" :key="option.value" class="dropdown-item"
              :class="{ 'selected': option.value.toString() === selectedValue }" @click.stop="selectOption(option)">
              {{ option.label }}
            </div>
          </template>
          <div v-else class="dropdown-item no-results">
            No results found
          </div>
        </div>
      </div>
    </div>

    <div v-if="showError && errorMessage" class="error-message">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'GradeDropdown',
  props: {
    value: {
      type: [String, Number],
      default: ''
    },
    designation: {
      type: String,
      required: false,
      default: ''
    },
    designationGrades: {
      type: Object,
      required: true
    },
    label: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: 'grade-dropdown'
    },
    placeholder: {
      type: String,
      default: 'Select Grade'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    maxHeight: {
      type: Number,
      default: 195
    },
    showError: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: 'This field is required'
    },
    debug: {
      type: Boolean,
      default: true // Enable debug by default
    }
  },
  emits: ['change'],
  data() {
    return {
      isOpen: false,
      selectedValue: this.value,
      selectedLabel: '',
      availableGrades: [],
      searchQuery: ''
    }
  },
  computed: {
    filteredGrades() {
      if (!this.searchQuery) return this.availableGrades;
      const query = this.searchQuery.toLowerCase().trim();
      return this.availableGrades.filter(option =>
        option.label.toLowerCase().includes(query)
      );
    }
  },
  created() {
    // Log in created hook
    console.log('[GradeDropdown] Created with props:', {
      designation: this.designation,
      value: this.value,
      designationGrades: this.designationGrades
    });
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        console.log('[GradeDropdown] Value changed:', newVal);
        this.selectedValue = newVal;
        this.updateSelectedLabel();
      }
    },
    designation: {
      immediate: true,
      handler(newDesignation) {
        console.log('[GradeDropdown] Designation changed:', newDesignation);
        this.updateAvailableGrades(newDesignation);
        this.updateSelectedLabel();
      }
    },
    designationGrades: {
      immediate: true,
      deep: true,
      handler(newGrades) {
        console.log('[GradeDropdown] DesignationGrades changed:', newGrades);
        this.updateAvailableGrades(this.designation);
        this.updateSelectedLabel();
      }
    }
  },


  methods: {
    updateAvailableGrades(designation) {
      console.log('[GradeDropdown] updateAvailableGrades called with:', designation);
      
      // If designation is undefined or empty, show no grades
      if (!designation) {
        console.log('[GradeDropdown] No designation provided, setting empty grades');
        this.availableGrades = [];
        return;
      }
      
      // Check the structure of designationGrades
      console.log('[GradeDropdown] DesignationGrades structure:', {
        hasDesignationGradesProperty: this.designationGrades && this.designationGrades.designationGrades ? 'yes' : 'no',
        keys: this.designationGrades ? Object.keys(this.designationGrades) : 'none'
      });
      
      // Check if designationGrades has a nested designationGrades property
      if (this.designationGrades && this.designationGrades.designationGrades) {
        console.log('[GradeDropdown] Using nested designationGrades property');
        this.availableGrades = this.designationGrades.designationGrades[designation] || [];
      } else if (this.designationGrades) {
        // Direct access to the object
        console.log('[GradeDropdown] Using direct designationGrades object');
        this.availableGrades = this.designationGrades[designation] || [];
      } else {
        console.log('[GradeDropdown] No designationGrades object found');
        this.availableGrades = [];
      }
      
      console.log('[GradeDropdown] Available Grades set to:', this.availableGrades);
    },
    updateSelectedLabel() {
      console.log('[GradeDropdown] updateSelectedLabel called with selectedValue:', this.selectedValue);
      
      if (!this.selectedValue) {
        console.log('[GradeDropdown] No selectedValue, clearing selectedLabel');
        this.selectedLabel = '';
        return;
      }
      
      const grade = this.availableGrades.find(g => g.value.toString() === this.selectedValue.toString());
      console.log('[GradeDropdown] Found grade for selectedValue:', grade);
      
      this.selectedLabel = grade ? grade.label : '';
      console.log('[GradeDropdown] Set selectedLabel to:', this.selectedLabel);
    },
    toggleDropdown() {
      if (!this.disabled) {
        this.isOpen = !this.isOpen;
        
        if (this.isOpen) {
          this.searchQuery = '';
          this.$nextTick(() => {
            if (this.$refs.searchInput) this.$refs.searchInput.focus();
          });
        }
      }
    },
    selectOption(option) {
      console.log('[GradeDropdown] Option selected:', option);
      this.selectedValue = option.value.toString();
      this.selectedLabel = option.label;
      this.$emit('change', option.value.toString());
      this.isOpen = false;
      this.searchQuery = '';
    },
    handleClickOutside(event) {
      const dropdownEl = this.$refs.dropdown;
      if (dropdownEl && !dropdownEl.contains(event.target)) {
        this.isOpen = false;
      }
    }
  },
  mounted() {
    console.log('[GradeDropdown] Mounted');
    document.addEventListener('click', this.handleClickOutside);
    this.updateAvailableGrades(this.designation);
    this.updateSelectedLabel();
  },
  beforeDestroy() {
    console.log('[GradeDropdown] Before destroy');
    document.removeEventListener('click', this.handleClickOutside);
  }
}
</script>

<style scoped>
.dropdown-container {
    margin-bottom: 1rem;
    position: relative;
    width: 100%;
}

.custom-dropdown {
    position: relative;
    width: 100%;
}

.dropdown-toggle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    line-height: 1.5;
    color: #495057;
    background-color: #fff;
    background-clip: padding-box;
    text-align: left;
    cursor: pointer;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    position: relative;
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    margin-bottom: 10px;
    box-sizing: border-box;
    height: 43px;
}

.selected-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
}

.dropdown-toggle:disabled {
    background-color: #e9ecef;
    opacity: 1;
    cursor: not-allowed;
}

.dropdown-toggle::after {
    display: none;
}

.dropdown-arrow {
    display: inline-block;
    width: 0;
    height: 0;
    margin-left: 8px;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid #495057;
    transition: transform 0.2s ease;
    flex-shrink: 0;
}

.dropdown-arrow.is-open {
    transform: rotate(180deg);
}

.dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 9999;
    width: 100%;
    overflow-y: auto;
    margin: 0.125rem 0 0;
    background-color: #fff;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 0.25rem;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.175);
}
.search-dropdown {
    padding: 8px;
    border-bottom: 1px solid #eee;
}
.search-field {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
}
.dropdown-items {
    max-height: 170px;
    overflow-y: auto;
}

.dropdown-item {
    display: block;
    width: 100%;
    padding: 0.375rem 0.75rem;
    clear: both;
    font-weight: 400;
    color: #212529;
    text-align: left;
    white-space: nowrap;
    background-color: transparent;
    border: 0;
    cursor: pointer;
}

.dropdown-item:hover {
    color: #ffffff;
    text-decoration: none;
    background-color: #435851;
}

.dropdown-item.selected {
    color: #fff;
    background-color: #435851;
}

.dropdown-item.no-results {
    font-style: italic;
    color: #6c757d;
    cursor: default;
}

.is-invalid .dropdown-toggle {
    border-color: #dc3545;
}

.error-message {
    width: 100%;
    /* margin-top: 0.25rem; */
    font-size: 80%;
    color: #dc3545;
}

.dropdown-menu::-webkit-scrollbar {
    width: 6px;
}

.dropdown-menu::-webkit-scrollbar-track {
    background: #f8f9fa;
    border-radius: 3px;
}

.dropdown-menu::-webkit-scrollbar-thumb {
    background: #adb5bd;
    border-radius: 3px;
}

.dropdown-menu::-webkit-scrollbar-thumb:hover {
    background: #6c757d;
}
</style>


// WEBPACK FOOTER //
// src/components/UIComponents/GradeDropdown.vue